/**
 * Route-level error boundary.
 *
 * A React crash unmounts the tree, and without a boundary that means a black
 * screen — the learner assumes the whole thing broke and their answers went
 * with it. This shows a calm, honest fallback ("your answers are safe"), offers
 * one retry and one way out, and reports the crash with route and component
 * stack so the same failure is debuggable without asking them to reproduce it.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportClientError, decodeReactError } from "@/lib/errorReporting";

type Props = {
  children: ReactNode;
  /** Names the guarded surface in the report, e.g. "AssessmentV2Result". */
  name: string;
  /** Extra debugging inputs the URL does not carry. */
  context?: Record<string, unknown>;
  /** Where "Take me somewhere safe" goes. */
  fallbackHref?: string;
};

type State = { error: Error | null; info: ErrorInfo | null };

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info });
    reportClientError({
      source: "react_boundary",
      error,
      componentStack: info.componentStack,
      boundary: this.props.name,
      context: this.props.context,
    });
  }

  private retry = () => this.setState({ error: null, info: null });

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const decoded = decodeReactError(error.message);
    const href = this.props.fallbackHref ?? "/dashboard";

    return (
      <main className="min-h-screen bg-background px-5 py-16 text-foreground">
        <div className="mx-auto max-w-md space-y-6 text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-destructive/15"
            aria-hidden="true"
          >
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">This screen stumbled</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Not your fault, and nothing is lost — your answers and your results are saved.
              We've logged what happened so we can fix it. Try loading this screen again.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button onClick={this.retry} className="min-h-11 gap-2">
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button asChild variant="outline" className="min-h-11">
              <a href={href}>Take me somewhere safe</a>
            </Button>
          </div>

          {/* The decoded invariant, only where it helps rather than worries. */}
          {import.meta.env.DEV && (
            <details className="rounded-2xl border border-border/60 bg-card/60 p-4 text-left">
              <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Developer detail
              </summary>
              <p className="mt-2 text-xs font-mono text-destructive">{decoded.message}</p>
              {decoded.hint && (
                <p className="mt-1 text-xs text-muted-foreground">{decoded.hint}</p>
              )}
              <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground">
                {(error.stack ?? "") + (info?.componentStack ?? "")}
              </pre>
            </details>
          )}
        </div>
      </main>
    );
  }
}

export default RouteErrorBoundary;
